import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';
import { AuthContext } from '../context/AuthContext';
import '../styles/pages/ProfilePage.css';
import MessageModal from '../components/MessageModal';
import { validateMobile } from '../utils/validation';

const defaultProfilePic = 'https://placehold.co/100x100/aabbcc/ffffff?text=User';

const getFullImageUrl = (relativePath) => {
  if (!relativePath) return defaultProfilePic;
  return `${relativePath}?t=${new Date().getTime()}`;
};

// ----------------- ProfilePage Main Component -----------------


const ProfilePage = ({ isAdmin = false }) => {
  const { user, token, updateUser: updateUserInContext } = useContext(AuthContext);

  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    nic: '',
    department: '',
    indexNumber: '',
    role: '',
    mobile: '',
  });

  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [messageModal, setMessageModal] = useState({ show: false, title: '', message: '', onConfirm: () => { } });
  const [error, setError] = useState('');

  const resetFormState = useCallback(() => {
    if (user) {
      setEditFormData({
        name: user.name || '',
        email: user.email || '',
        nic: user.nic || '',
        department: user.department || '',
        indexNumber: user.indexNumber || '',
        role: user.role || '',
        mobile: user.mobile || '',
      });
      setProfilePicturePreview(getFullImageUrl(user.profilePicture));
    }
  }, [user]);

  useEffect(() => {
    resetFormState();
  }, [user, resetFormState]);


  const closeMessageModal = () => {
    setMessageModal({ show: false, title: '', message: '', onConfirm: () => { } });
    setError('');
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setShowPasswordChange(false);
    setProfilePicturePreview(getFullImageUrl(user.profilePicture));
    setProfilePictureFile(null);
  };

  const handlePasswordChangeClick = () => {
    setShowPasswordChange(true);
    setIsEditing(false);
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setError('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setShowPasswordChange(false);
    setError('');
    resetFormState();
    setProfilePictureFile(null);
  };


  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
    setError('');
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData({ ...passwordFormData, [name]: value });
    setError('');
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      setProfilePicturePreview(URL.createObjectURL(file));
      setError('');
    } else {
      setProfilePictureFile(null);
      setProfilePicturePreview(getFullImageUrl(user.profilePicture));
    }
  };

  // -------------- Save Profile Changes ----------------

  const handleSave = async (e) => {
    e.preventDefault();

    if (!editFormData.name.trim()) return setError("Full Name cannot be empty.");
    // if (!validateEmail(editFormData.email)) return setError("Please enter a valid email address.");
    // if (!validateNic(editFormData.nic)) return setError("Please enter a valid NIC.");
    if (!validateMobile(editFormData.mobile)) return setError("Please enter a valid 10-digit mobile number.");
    // if (editFormData.role === "Student" && !editFormData.indexNumber.trim())
    //   return setError("Student must provide an Index Number.");
    if (editFormData.department.trim() === "")
      return setError("Please select a department.");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', editFormData.name);
      // formDataToSend.append('email', editFormData.email);
      // formDataToSend.append('nic', editFormData.nic);
      formDataToSend.append('department', editFormData.department);
      formDataToSend.append('role', editFormData.role);
      formDataToSend.append('mobile', editFormData.mobile);
      // if (editFormData.role === 'Student') {
      //   formDataToSend.append('indexNumber', editFormData.indexNumber);
      // }
      if (profilePictureFile) {
        formDataToSend.append('profilePicture', profilePictureFile);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${user._id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend,
      });

      const data = await response.json();
      if (response.ok) {
        updateUserInContext(data);
        setProfilePicturePreview(getFullImageUrl(data.profilePicture));
        setMessageModal({ show: true, title: 'Success', message: 'Profile updated successfully!', onConfirm: closeMessageModal });
        setIsEditing(false);
        setProfilePictureFile(null);
      } else {
        setError(data.message || 'Failed to update profile.');
        setMessageModal({ show: true, title: 'Error', message: data.message || 'Failed to update profile.', onConfirm: closeMessageModal });
      }
    } catch (apiError) {
      console.error('Profile update error:', apiError);
      setError('Network error or server unavailable.');
      setMessageModal({ show: true, title: 'Error', message: 'Network error or server unavailable.', onConfirm: closeMessageModal });
    }
  };

  // -------------- Handle Password Change ----------------
  const handleChangePassword = async (e) => {
    e.preventDefault();

    const { currentPassword, newPassword, confirmNewPassword } = passwordFormData;

    // Client-side validation
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      return setError("All password fields are required.");
    }
    if (newPassword.length < 6) {
      return setError("New password must be at least 6 characters long.");
    }
    if (newPassword !== confirmNewPassword) {
      return setError("New passwords do not match.");
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${user._id}/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmNewPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessageModal({
          show: true,
          title: 'Success',
          message: 'Password changed successfully!',
          onConfirm: () => {
            closeMessageModal();
            setShowPasswordChange(false); // Hide the form on success
            setPasswordFormData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
          },
        });
      } else {
        setError(data.message || 'Failed to change password. Please check your current password.');
        setMessageModal({ show: true, title: 'Error', message: data.message || 'Failed to change password.', onConfirm: closeMessageModal });
      }
    } catch (apiError) {
      console.error('Password change error:', apiError);
      setError('Network error or server unavailable.');
      setMessageModal({ show: true, title: 'Error', message: 'Network error or server unavailable.', onConfirm: closeMessageModal });
    }
  };

  // ----------------- UI Rendering -----------------
  const profileContent = (
    <div className="profile-card">
      <h2>{user.name}</h2>
      {/* Profile Picture */}
      <div className="profile-picture-container">
        <img src={profilePicturePreview} alt="Profile" className="profile-picture"
          onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }} />
        {isEditing && (
          <button type="button" className="change-photo-btn" onClick={() => fileInputRef.current.click()}>
            Change Photo
          </button>
        )}
        <input type="file" ref={fileInputRef} onChange={handleProfilePictureChange}
          style={{ display: 'none' }} accept="image/*" />
      </div>

      {/* Display View */}
      {!isEditing && !showPasswordChange ? (
        <>
          <div className="profile-info-group"><strong>Name:</strong><span>{user.name}</span></div>
          <div className="profile-info-group"><strong>Email:</strong><span>{user.email}</span></div>
          <div className="profile-info-group"><strong>NIC:</strong><span>{user.nic || 'N/A'}</span></div>
          <div className="profile-info-group"><strong>Mobile:</strong><span>{user.mobile || 'N/A'}</span></div>
          <div className="profile-info-group"><strong>Role:</strong><span>{user.role}</span></div>
          <div className="profile-info-group"><strong>Department:</strong><span>{user.department || 'N/A'}</span></div>
          {user.role?.toLowerCase() === 'student' && (
            <div className="profile-info-group"><strong>Index Number:</strong><span>{user.indexNumber || 'N/A'}</span></div>
          )}
          <div className="profile-actions">
            <button onClick={handleEditClick} className="edit-profile-btn">Edit Profile</button>
            <button onClick={handlePasswordChangeClick} className="change-password-btn">Change Password</button>
          </div>
        </>
      ) : null}

      {/* Edit Profile Form */}
      {isEditing && (
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input type="text" id="name" name="name" value={editFormData.name} onChange={handleEditFormChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input type="email" id="email" name="email" value={editFormData.email} onChange={handleEditFormChange} required disabled />
          </div>
          <div className="form-group">
            <label htmlFor="nic">NIC Number:</label>
            <input type="text" id="nic" name="nic" value={editFormData.nic} onChange={handleEditFormChange} required disabled />
          </div>
          <div className="form-group">
            <label htmlFor="mobile">Mobile Number:</label>
            <input type="text" id="mobile" name="mobile" value={editFormData.mobile} onChange={handleEditFormChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="role">Role:</label>
            <input type="text" id="role" name="role" value={editFormData.role} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="department">Department:</label>
            <input type="text" id="department" name="department" value={editFormData.department} onChange={handleEditFormChange} required disabled />

          </div>
          {editFormData.role?.toLowerCase() === 'student' && (
            <div className="form-group">
              <label htmlFor="indexNumber">Index Number:</label>
              <input type="text" id="indexNumber" name="indexNumber"
                value={editFormData.indexNumber} onChange={handleEditFormChange} required disabled />
            </div>
          )}
          {error && <p className="profile-error">{error}</p>}
          <div className="profile-actions">
            <button type="submit" className="save-profile-btn">Save Changes</button>
            <button type="button" onClick={handleCancelEdit} className="cancel-profile-btn">Cancel</button>
          </div>
        </form>
      )}

      {/* Change Password Form */}
      {showPasswordChange && (
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password:</label>
            <input type="password" id="currentPassword" name="currentPassword"
              value={passwordFormData.currentPassword} onChange={handlePasswordFormChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">New Password:</label>
            <input type="password" id="newPassword" name="newPassword"
              value={passwordFormData.newPassword} onChange={handlePasswordFormChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="confirmNewPassword">Confirm New Password:</label>
            <input type="password" id="confirmNewPassword" name="confirmNewPassword"
              value={passwordFormData.confirmNewPassword} onChange={handlePasswordFormChange} required />
          </div>
          {error && <p className="profile-error">{error}</p>}
          <div className="profile-actions">
            <button type="submit" className="save-profile-btn">Save Password</button>
            <button type="button" onClick={handleCancelEdit} className="cancel-profile-btn">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );

  if (!user) {
    if (isAdmin) {
      return <p style={{ textAlign: 'center', marginTop: '50px', fontSize: '1.5rem' }}>Loading user profile...</p>;
    } else {
      return (
        <div className="profile-container">
          <Header user={null} />
          <div className="profile-layout">
            <Sidebar />
            <main className="profile-content">
              <p style={{ textAlign: 'center', marginTop: '50px', fontSize: '1.5rem' }}>Loading user profile...</p>
            </main>
          </div>
          <Footer />
        </div>
      );
    }
  }

  if (isAdmin) {
    return (
      <div className="profile-content">
        {profileContent}
        <MessageModal
          show={messageModal.show}
          title={messageModal.title}
          message={messageModal.message}
          onConfirm={messageModal.onConfirm}
        />
      </div>
    );
  } else {
    return (
      <div className="profile-container">
        <div className="profile-layout">
          <main className="profile-content">
            {profileContent}
          </main>
        </div>
        <Footer />
        <MessageModal
          show={messageModal.show}
          title={messageModal.title}
          message={messageModal.message}
          onConfirm={messageModal.onConfirm}
        />
      </div>
    );
  }
};

export default ProfilePage;
